import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { TabBar } from '@/components/admin/sessions/sessions-tabs';
import { SessionVariables } from '@/components/admin/sessions/walk/session-variables';
import { SessionWalk } from '@/components/admin/sessions/walk/session-walk';
import type { SessionDetail } from '@matter_extractor/shared';
import { type ReactNode, useEffect, useRef, useState } from 'react';

/**
 * The two tabs under the eight figures of a session (§12.2, issue #172):
 * **Parcours**, open by default - the Lecture à blanc and the fil of the pages,
 * unchanged - and **Variables**, the inventory.
 *
 * ## The tab is the route's, the ring is this component's
 *
 * The tab travels in the URL (`?tab=variables`, Parcours writing none), so it
 * comes in as a prop and leaves by `onSelectTab`: a pasted link opens the right
 * tab, and this component never learns the route. What it holds itself is the
 * one thing no URL should carry - which card to ring on the way back from a
 * line of the inventory. The ring lasts 1.8 s, the maquette's, and is then
 * dropped, so a card is never left ringed by a reload of the query.
 *
 * ## One tab mounted at a time
 *
 * The maquette hides the other panel; this unmounts it. The walk is drawn from
 * the detail already in the cache and the trial reads under its own keys, so
 * nothing is fetched twice - and a card that MOUNTS with its ring plays the
 * animation without any class juggling to restart it.
 */

export const SESSION_READING_TABS = [
  { id: 'parcours', label: 'Parcours' },
  { id: 'variables', label: 'Variables' },
] as const;

export type SessionReadingTab = (typeof SESSION_READING_TABS)[number]['id'];

/** How long a card stays ringed: the maquette's animation. */
const FLASH_MS = 1800;

export interface SessionReadingProps {
  readonly detail: SessionDetail;
  readonly tab: SessionReadingTab;
  readonly onSelectTab: (tab: SessionReadingTab) => void;
  /**
   * The section « Lecture à blanc » (noyau.md §9.4), mounted by the route: it
   * reads and writes trials through the query client, which the walk alone
   * does not need (`sessions-parcours.test.tsx` mounts it without one).
   */
  readonly trial?: ReactNode;
}

export function SessionReading({ detail, tab, onSelectTab, trial = null }: SessionReadingProps) {
  const [flash, setFlash] = useState<number | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // The switch lands through the router, so the tab may still be Variables
    // on the render that set the ring: wait for the walk to be mounted.
    if (tab !== 'parcours' || flash === null) return;
    const card = root.current?.querySelector(`[data-visit="${flash}"]`);
    // Optional call: jsdom has no `scrollIntoView`.
    card?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(() => setFlash(null), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [tab, flash]);

  const select = (next: SessionReadingTab): void => {
    setFlash(null);
    onSelectTab(next);
  };

  return (
    <div ref={root} className="flex flex-col gap-5">
      <TabBar
        tabs={SESSION_READING_TABS}
        active={tab}
        onSelect={select}
        testId={SESSIONS_TEST_IDS.walkTabs}
        tabTestId={SESSIONS_TEST_IDS.walkTab}
      />

      {tab === 'variables' ? (
        <SessionVariables
          entries={detail.variables}
          retained={detail.noyau?.retained ?? null}
          onOpenPage={(visit) => {
            setFlash(visit);
            onSelectTab('parcours');
          }}
        />
      ) : (
        <>
          {trial}

          <h2 className="font-semibold text-gray-600 text-micro uppercase tracking-wide">
            Le parcours
          </h2>
          <SessionWalk detail={detail} flashVisit={flash} />

          <p className="max-w-[80ch] text-caption text-gray-500">
            L’heure d’arrivée sur une page est celle de l’avancée qui l’a servie : la session
            horodate chaque avancée, une ligne par page traversée. Une page entrée deux fois - une
            reprise - apparaît deux fois.
            {detail.noyau === null
              ? null
              : ' Sur une carte de noyau, seul l’opérateur voit le nom, le triplet et la couleur : la personne n’a jamais vu que le texte. Le second choix est enregistré pour la mesure ; il ne change jamais le noyau retenu.'}
          </p>
        </>
      )}
    </div>
  );
}
